import { createRoute, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { useServiceNiceName } from '@puffin/service-registry/service-name';
import { rootRoute } from '../services/routing/rootRoute';
import { routeLabels } from '../services/routing/routeMetadata';

const serviceRegistryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'services',
  component: Outlet,
  staticData: { ...routeLabels('Service Registry'), breadcrumbTo: '/services/overview' },
});

const serviceRegistryIndexRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: '/',
  beforeLoad: () => redirect({ to: '/services/overview', throw: true }),
});

const overviewRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: 'overview',
  component: lazyRouteComponent(() => import('@puffin/service-registry/overview-page'), 'OverviewPage'),
  staticData: { ...routeLabels('Overview'), navigationPath: '/services/overview' },
});

const serviceDetailRoute = createRoute(
  (() => {
    const LazyServicePage = lazyRouteComponent(() => import('@puffin/service-registry/service-page'), 'ServicePage') as ComponentType<{ serviceId: string }>;
    const useServiceName = (): string => `Details: ${useServiceNiceName(serviceDetailRoute.useParams().serviceId)}`;
    const ServicePage = () => <LazyServicePage serviceId={serviceDetailRoute.useParams().serviceId} />;
    return {
      getParentRoute: () => serviceRegistryRoute,
      path: '$serviceId',
      component: ServicePage,
      staticData: {
        useBreadcrumbLabel: useServiceName,
        useDocumentTitlePrefix: useServiceName,
        navigationPath: '/services/overview',
      },
    };
  })(),
);

const descriptionsRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: 'descriptions',
  component: lazyRouteComponent(() => import('@puffin/service-registry/descriptions-page'), 'DescriptionsPage'),
  staticData: { ...routeLabels('Descriptions'), navigationPath: '/services/descriptions' },
});

const dataRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: 'data',
  component: lazyRouteComponent(() => import('@puffin/service-registry/data-page'), 'DataPage'),
  staticData: { ...routeLabels('Processed & Stored Data'), navigationPath: '/services/data' },
});

const portsRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: 'ports',
  component: lazyRouteComponent(() => import('@puffin/service-registry/ports-page'), 'PortsPage'),
  staticData: { ...routeLabels('Used Ports'), navigationPath: '/services/ports' },
});

const dependenciesRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: 'dependencies',
  component: lazyRouteComponent(() => import('@puffin/service-registry/dependencies-page'), 'DependenciesPage'),
  staticData: { ...routeLabels('Dependencies'), navigationPath: '/services/dependencies' },
});

const graphqlRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: 'graphql',
  component: lazyRouteComponent(() => import('@puffin/service-registry/graphql-page'), 'GraphqlPage'),
  staticData: { ...routeLabels('GraphQL Contracts'), navigationPath: '/services/graphql' },
});

const messagesRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: 'messages',
  component: lazyRouteComponent(() => import('@puffin/service-registry/messages-page'), 'MessagesPage'),
  staticData: { ...routeLabels('Messaging Contracts'), navigationPath: '/services/messages' },
});

const validationRoute = createRoute({
  getParentRoute: () => serviceRegistryRoute,
  path: 'validation',
  component: lazyRouteComponent(() => import('@puffin/service-registry/validation-page'), 'ValidationPage'),
  staticData: { ...routeLabels('Validate Service Spec'), navigationPath: '/services/validation' },
});

export const serviceRegistryRouteTree = serviceRegistryRoute.addChildren([
  serviceRegistryIndexRoute,
  overviewRoute,
  serviceDetailRoute,
  descriptionsRoute,
  dataRoute,
  portsRoute,
  dependenciesRoute,
  graphqlRoute,
  messagesRoute,
  validationRoute,
]);
